import {
  Button,
  Modal,
  StatusPill,
  addMonths,
  scrollMotion,
  type StatusTone,
} from '@hue/design-system';
import {
  ArrowUp,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Minus,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../lib/time-zone.js';
import { useCompanyScope } from '../company-context.js';
import { stepDone, type StepState } from './guide-model.js';
import type { GuideDefinition, GuideStep, GuideStepTag } from './guide-types.js';

const TAGS: Record<GuideStepTag, { label: string; tone: StatusTone }> = {
  blocks: { label: 'Impede fechar', tone: 'danger' },
  warns: { label: 'O fechamento avisa', tone: 'progress' },
  silent: { label: 'O sistema não avisa se faltar', tone: 'warning' },
  optional: { label: 'Se a empresa usar', tone: 'progress' },
  read: { label: 'Só leitura', tone: 'neutral' },
  final: { label: 'Fecha o mês', tone: 'positive' },
  extra: { label: 'Só se precisar', tone: 'neutral' },
};

const EFFECT: Record<string, string> = {
  positive: 'bg-positive/10 text-positive',
  info: 'bg-info/10 text-info',
  warning: 'bg-warning/10 text-warning',
  neutral: 'bg-content/5 text-content-muted',
};

const inMonth = (text: string, monthName: string) => text.replaceAll('{mes}', monthName);
const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function stepHint(step: GuideStep, state: StepState): string | null {
  if (state.kind === 'not-applicable') return 'Não se aplica';
  if (state.kind === 'confirmed') return `Conferido por ${state.by ?? 'alguém da equipe'}`;
  if (state.kind === 'measured-done') return 'Concluído pelo sistema';
  if (state.kind === 'measured-unavailable') return 'Não foi possível conferir';
  return step.tag === 'optional' ? 'Se a empresa usar' : null;
}

function StepDot({
  step,
  state,
  position,
}: {
  step: GuideStep;
  state: StepState;
  position: number;
}) {
  const base =
    'grid size-[22px] shrink-0 place-items-center rounded-full text-[11px] font-semibold';
  if (state.kind === 'not-applicable') {
    return (
      <span className={`${base} border border-dashed border-surface-border text-content-muted`}>
        <Minus className="size-3" aria-hidden="true" />
      </span>
    );
  }
  if (!step.appendix && stepDone(state)) {
    return (
      <span className={`${base} bg-positive text-surface-card`}>
        <Check className="size-3" strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className={`${base} border border-surface-border tabular-nums text-content-muted`}>
      {step.appendix ? '+' : position}
    </span>
  );
}

function ActionCard({
  step,
  state,
  canReach,
  onGo,
}: {
  step: GuideStep;
  state: StepState;
  canReach: boolean;
  onGo: () => void;
}) {
  const { timeZone } = useCompanyScope();
  const go = canReach ? (
    <Button
      variant="outline"
      size="sm"
      className="h-auto min-h-9 max-w-full whitespace-normal py-2 text-left"
      onClick={onGo}
    >
      Ir para {step.target.label}
      <ArrowUpRight className="size-4" aria-hidden="true" />
    </Button>
  ) : (
    <span className="text-xs text-warning">
      Você não tem acesso a esta tela. Peça a quem tem para fazer este passo.
    </span>
  );
  const done = stepDone(state);
  const title = done ? 'Já feito' : state.kind === 'reference' ? 'Este passo' : 'O que fazer agora';
  let sentence: string;
  let note: string | null = null;
  switch (state.kind) {
    case 'measured-done':
      sentence = state.text;
      note =
        'Concluído pelo sistema. Se aparecer algo novo, o passo volta a ficar pendente sozinho.';
      break;
    case 'measured-todo':
      sentence = state.text;
      note = 'Não precisa marcar nada: o sistema conclui este passo quando o trabalho acabar.';
      break;
    case 'measured-unavailable':
      sentence = state.text;
      note =
        'Abra a tela do passo ou peça a alguém com acesso para conferir. Não é possível marcar este passo manualmente.';
      break;
    case 'confirmed':
      sentence = `Conferido por ${state.by ?? 'alguém da equipe'} em ${formatDateTime(state.on, timeZone)}.`;
      break;
    case 'not-applicable':
      sentence = 'Marcado como “Não se aplica” neste mês.';
      break;
    case 'to-confirm':
      sentence = state.text;
      note = 'O sistema não tem como saber se ficou completo. Quando terminar, confirme aqui.';
      break;
    case 'reference':
      sentence = state.text;
      break;
  }
  return (
    <div
      className={`grid min-w-0 gap-1.5 rounded-2xl border p-4 ${
        done ? 'border-positive/40 bg-positive/5' : 'border-brand-500/60 bg-brand-500/5'
      }`}
    >
      <p className={`text-xs font-semibold ${done ? 'text-positive' : 'text-brand-400'}`}>
        {title}
      </p>
      <p className="font-semibold leading-snug">{sentence}</p>
      {
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3 text-sm text-content-muted">
          <span className="min-w-0">{step.where}</span>
          {go}
        </div>
      }
      {note ? <p className="text-xs text-content-muted">{note}</p> : null}
    </div>
  );
}

function IfList({ items, monthName }: { items: GuideStep['cases']; monthName: string }) {
  return (
    <ul className="overflow-hidden rounded-xl border border-surface-border">
      {items!.map(([when, then]) => (
        <li
          key={when}
          className="grid gap-0.5 border-t border-surface-border px-3.5 py-2.5 text-sm first:border-t-0 sm:grid-cols-[2fr_3fr] sm:gap-3.5"
        >
          <strong className="font-semibold">{inMonth(when, monthName)}</strong>
          <span>{inMonth(then, monthName)}</span>
        </li>
      ))}
    </ul>
  );
}

/// O padrão de todo roteiro (ADR 0084): etapas à esquerda, passo à direita, ações no rodapé.
export function GuideModal({
  guide,
  steps,
  states,
  index,
  month,
  monthName,
  lastMonth,
  done,
  total,
  unitName,
  needsUnit,
  canConfirm,
  progressAvailable,
  loadError,
  busy,
  canReach,
  onMonth,
  onStep,
  onClose,
  onGo,
  onHelp,
  onConfirm,
  onUndo,
}: {
  guide: GuideDefinition;
  steps: readonly GuideStep[];
  states: readonly StepState[];
  index: number;
  month: string;
  monthName: string;
  lastMonth: string;
  done: number;
  total: number;
  unitName: string | null;
  needsUnit: boolean;
  canConfirm: boolean;
  progressAvailable: boolean;
  loadError: string | null;
  busy: boolean;
  canReach: (step: GuideStep) => boolean;
  onMonth: (month: string) => void;
  onStep: (index: number) => void;
  onClose: () => void;
  onGo: (step: GuideStep) => void;
  onHelp: (step: GuideStep) => void;
  onConfirm: (status: 'CONFIRMED' | 'NOT_APPLICABLE') => void;
  onUndo: () => void;
}) {
  const step = steps[index]!;
  const state = states[index]!;
  const [listOpen, setListOpen] = useState(false);
  const title = useRef<HTMLHeadingElement>(null);
  const detail = useRef<HTMLDivElement>(null);
  const counted = steps.filter((one) => !one.appendix);
  const positionOf = (one: GuideStep) => counted.indexOf(one) + 1;
  const label = step.appendix ? 'Anexo' : `Passo ${String(positionOf(step))} de ${String(total)}`;
  const tag = TAGS[step.tag];

  useEffect(() => {
    detail.current?.scrollTo?.({ top: 0 });
    title.current?.focus({ preventScroll: true });
  }, [step.key, month]);

  const shift = (months: number) => onMonth(addMonths(`${month}-01`, months).slice(0, 7));
  const canAnswer = !needsUnit && canConfirm && progressAvailable;
  const asksConfirmation = state.kind === 'to-confirm' && !needsUnit && progressAvailable;
  const canUndo = (state.kind === 'confirmed' || state.kind === 'not-applicable') && canAnswer;

  const list = steps.map((one, i) => {
    const heading = one.phase !== steps[i - 1]?.phase ? one.phase : null;
    const hint = stepHint(one, states[i]!);
    return (
      <div key={one.key}>
        {heading ? (
          <p className="px-2.5 pb-1 pt-3 text-xs font-semibold text-content-muted first:pt-1">
            {heading}
          </p>
        ) : null}
        <button
          type="button"
          aria-current={i === index ? 'step' : undefined}
          onClick={() => {
            onStep(i);
            setListOpen(false);
          }}
          className={`grid w-full grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5 rounded-xl px-2.5 py-1.5 text-left text-sm leading-snug transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
            i === index ? 'bg-brand-500/10 font-semibold text-brand-400' : 'hover:bg-content/5'
          }`}
        >
          <StepDot step={one} state={states[i]!} position={positionOf(one)} />
          <span className="min-w-0">
            {one.title}
            {hint ? (
              <span className="block text-xs font-normal text-content-muted">{hint}</span>
            ) : null}
          </span>
        </button>
      </div>
    );
  });

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={guide.title}
      description={unitName ? `Roteiro · ${unitName}` : 'Roteiro'}
      className="h-[calc(100dvh-1.5rem)] max-w-5xl sm:h-[calc(100dvh-3rem)]"
      bodyClassName="flex flex-col overflow-hidden p-0 sm:p-0"
      headerClassName="py-3 sm:py-5"
      footerClassName="py-3 sm:py-4"
      footer={
        <div className="grid w-full gap-2">
          {asksConfirmation ? (
            <p className="text-xs text-content-muted">
              {canConfirm ? (
                <>
                  Com seu nome e data:{' '}
                  <strong className="text-content">
                    {inMonth(step.confirmation ?? '', monthName)}
                  </strong>
                  . Não altera lançamentos.
                </>
              ) : (
                'Sua permissão não inclui confirmar passos de roteiros.'
              )}
            </p>
          ) : null}
          <div className="grid grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="px-2 sm:px-4"
              aria-label="Passo anterior"
              disabled={index === 0}
              onClick={() => onStep(index - 1)}
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="px-2 sm:px-4"
              aria-label="Voltar ao topo do passo"
              onClick={() => {
                detail.current?.scrollTo?.({ top: 0, behavior: scrollMotion() });
                title.current?.focus({ preventScroll: true });
              }}
            >
              <ArrowUp className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Topo</span>
            </Button>
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
              {asksConfirmation && canConfirm && step.tag === 'optional' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onConfirm('NOT_APPLICABLE')}
                >
                  Não se aplica
                </Button>
              ) : null}
              {canUndo ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto min-h-9 max-w-full whitespace-normal px-2 py-1 sm:px-4"
                  disabled={busy}
                  onClick={onUndo}
                >
                  {state.kind === 'confirmed' ? 'Desfazer a confirmação' : 'Voltar a pendente'}
                </Button>
              ) : null}
              <Button
                variant={asksConfirmation && canConfirm ? 'outline' : 'primary'}
                aria-label="Próximo"
                className="px-2 sm:px-4"
                size="sm"
                disabled={index === steps.length - 1}
                onClick={() => onStep(index + 1)}
              >
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight className="size-4" aria-hidden="true" />
              </Button>
              {asksConfirmation && canConfirm ? (
                <Button
                  size="sm"
                  className="px-2 sm:px-4"
                  aria-label="Confirmar e seguir"
                  disabled={busy}
                  onClick={() => onConfirm('CONFIRMED')}
                >
                  <Check className="size-4" aria-hidden="true" />
                  <span className="sm:hidden">Confirmar</span>
                  <span className="hidden sm:inline">Confirmar e seguir</span>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      }
    >
      <div className="grid shrink-0 gap-2 border-b border-surface-border px-4 py-2 sm:px-7 sm:py-3">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div role="group" aria-label="Mês do roteiro" className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Mês anterior"
              onClick={() => shift(-1)}
              className="grid size-9 place-items-center rounded-lg border border-surface-border hover:bg-content/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            <strong aria-live="polite" className="min-w-40 text-center text-sm font-semibold">
              {capital(monthName)}
            </strong>
            <button
              type="button"
              aria-label="Próximo mês"
              disabled={month >= lastMonth}
              onClick={() => shift(1)}
              className="grid size-9 place-items-center rounded-lg border border-surface-border enabled:hover:bg-content/5 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
          </div>
          <p className="flex min-w-48 flex-1 items-center gap-2.5 text-xs tabular-nums text-content-muted">
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-content/10">
              <span
                className="block h-full rounded-full bg-brand-500"
                style={{ width: `${String(total ? (done / total) * 100 : 0)}%` }}
              />
            </span>
            {done} de {total} passos
          </p>
        </div>
        {loadError ? (
          <p role="alert" className="text-sm text-danger">
            {loadError}
          </p>
        ) : null}
        {needsUnit ? (
          <p role="status" className="text-sm text-warning">
            Escolha uma unidade no seletor do topo para acompanhar o progresso. Cada unidade fecha o
            próprio mês.
          </p>
        ) : null}
      </div>
      <div className="relative grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
        <button
          type="button"
          aria-expanded={listOpen}
          onClick={() => setListOpen(!listOpen)}
          className="flex min-h-11 items-center justify-between gap-2 border-b border-surface-border px-4 py-2 text-left text-sm font-semibold lg:hidden"
        >
          <span className="min-w-0">
            {label}: {step.title}
          </span>
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        </button>
        <nav
          aria-label="Passos do roteiro"
          className={`${
            listOpen ? 'absolute inset-x-0 bottom-0 top-11 z-10 block' : 'hidden'
          } overflow-y-auto bg-surface-card p-2.5 lg:static lg:block lg:border-r lg:border-surface-border lg:bg-content/[.02]`}
        >
          {list}
        </nav>
        <div
          ref={detail}
          role="region"
          aria-label="Conteúdo do passo"
          className="grid min-h-0 min-w-0 content-start gap-5 overflow-y-auto px-4 py-5 sm:px-7"
        >
          <div className="grid min-w-0 gap-1.5">
            <p className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
              {label}
              <StatusPill tone={tag.tone}>{tag.label}</StatusPill>
            </p>
            <h3
              ref={title}
              tabIndex={-1}
              className="break-words text-xl font-semibold leading-tight tracking-tight outline-none sm:text-2xl"
            >
              {step.title}
            </h3>
            <p className="max-w-[68ch] text-content-muted">{inMonth(step.summary, monthName)}</p>
          </div>
          <ActionCard step={step} state={state} canReach={canReach(step)} onGo={() => onGo(step)} />
          {step.why ? (
            <p className="max-w-[70ch] border-l-2 border-brand-500 pl-3 text-sm text-content-muted">
              <strong className="font-semibold text-content">Por que nesta ordem:</strong>{' '}
              {step.why}
            </p>
          ) : null}
          <section className="grid gap-2">
            <h4 className="text-sm font-semibold">Como fazer</h4>
            <ol className="grid max-w-[72ch] list-decimal gap-2 pl-6 marker:font-semibold marker:text-brand-400">
              {step.how.map((line) => (
                <li key={line}>{inMonth(line, monthName)}</li>
              ))}
            </ol>
          </section>
          {step.cases ? (
            <section className="grid gap-2">
              <h4 className="text-sm font-semibold">Depende do caso</h4>
              <IfList items={step.cases} monthName={monthName} />
            </section>
          ) : null}
          {step.problems ? (
            <section className="grid gap-2">
              <h4 className="text-sm font-semibold">Se algo faltar ou der errado</h4>
              <IfList items={step.problems} monthName={monthName} />
            </section>
          ) : null}
          {step.effects ? (
            <section className="grid gap-2">
              <h4 className="text-sm font-semibold">O que muda depois</h4>
              <div className="flex flex-wrap gap-1.5">
                {step.effects.map(([text, tone]) => (
                  <span
                    key={text}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium ${EFFECT[tone]!}`}
                  >
                    {text}
                  </span>
                ))}
              </div>
            </section>
          ) : null}
          {step.help ? (
            <div>
              <button
                type="button"
                onClick={() => onHelp(step)}
                className="text-sm font-semibold text-brand-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                Mais detalhes na Ajuda
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
